import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft } from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FieldPath, useForm } from "react-hook-form";
import { ScrollView, View, YStack } from "tamagui";
import { z } from "zod";

import {
  getGetUserProfileQueryKey,
  useUpsertUserProfile,
} from "@/api/generated";
import {
  type GenderIdentity,
  type Pronoun,
  type UserProfileRequest,
  VerificationStatus,
} from "@/api/generated/models";
import { useGetUserProfile } from "@/api/queries";
import { Header } from "@/components";
import { Button } from "@/components/Button";
import { Heading, Paragraph } from "@/components/Typography";
import { shortBrandName } from "@/config/brand";
import { useZipcodeValidation } from "@/forms/hooks/useZipcodeValidation";
import {
  validateDate,
  validateMaxAge,
  validateMinAge,
  validateMmDdYyyyFormat,
} from "@/utils/validators";
import { AddressFields, addressSchema } from "./AddressFields";
import { PersonalInfoFields } from "./PersonalInfoFields";

export const dateOfBirthZodString = z
  .string()
  .min(1, "Date of birth is required")
  .refine(validateMmDdYyyyFormat, {
    message: "Please use mm/dd/yyyy with a 4-digit year",
  })
  .refine((value) => validateDate(value), {
    message: "Invalid date",
  })
  .refine((value) => validateMaxAge(value), {
    message: "Please enter a valid date of birth",
  })
  .refine((value) => validateMinAge(value), {
    message: "You must be at least 18 years old",
  });

export const profileBuilderSchema = z.object({
  firstName: z.string().min(1, "Legal first name is required"),
  lastName: z.string().min(1, "Legal last name is required"),
  preferredName: z.string().optional(),
  dateOfBirth: dateOfBirthZodString,
  sexAssignedAtBirth: z.string().min(1, "Sex assigned at birth is required"),
  genderIdentity: z.string().optional(),
  pronouns: z.string().optional(),
  email: z.email("Email address is required"),

  ...addressSchema.shape,
  stateOfResidence: z.string().optional(),
});

export type ProfileFormData = z.infer<typeof profileBuilderSchema>;

const PROFILE_FORM_FIELD_PAGES = [
  [
    "firstName",
    "lastName",
    "dateOfBirth",
    "sexAssignedAtBirth",
    "genderIdentity",
    "pronouns",
    "email",
  ],
  ["streetAddress", "city", "state", "zipCode"],
] as const satisfies readonly (readonly FieldPath<ProfileFormData>[])[];

const EMPTY_PROFILE_VALUES: ProfileFormData = {
  firstName: "",
  lastName: "",
  preferredName: "",
  dateOfBirth: "",
  sexAssignedAtBirth: "",
  genderIdentity: "",
  pronouns: "",
  email: "",
  streetAddress: "",
  streetAddress2: "",
  city: "",
  state: "",
  zipCode: "",
  stateOfResidence: "",
};

export type ProfileFormLayoutProps = {
  fields: ReactNode;
  footer: ReactNode;
  currentPage: number;
  handleBack: () => void;
  scrollViewRef: RefObject<ScrollView | null>;
};

export const ProfileForm = ({
  onSuccess,
  onError,
  renderLayout,
}: {
  onSuccess: () => void;
  onError: () => void;
  renderLayout?: (props: ProfileFormLayoutProps) => ReactNode;
}) => {
  const queryClient = useQueryClient();
  const { show } = useToastController();
  const { mutate: upsertProfile, isPending: isSubmitting } =
    useUpsertUserProfile({
      mutation: { meta: { suppressErrorToast: true } },
    });
  const { data: userProfile } = useGetUserProfile();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const scrollViewRef = useRef<ScrollView>(null);

  const isIdVerified =
    userProfile?.idVerification?.status === VerificationStatus.approved;

  const profileValues = useMemo<ProfileFormData>(() => {
    if (!userProfile) return EMPTY_PROFILE_VALUES;
    const { zipCode, residingState, ...rest } = userProfile;

    return {
      ...rest,
      stateOfResidence: residingState,
      zipCode: zipCode?.slice(0, 5) || "",
    };
  }, [userProfile]);

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileBuilderSchema),
    mode: "onBlur",
    shouldFocusError: true,
    shouldUnregister: false,
    defaultValues: EMPTY_PROFILE_VALUES,
    values: profileValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    setFocus,
  } = form;

  const { zipcodeError, isValidatingZipcode } = useZipcodeValidation({
    control,
  });

  useEffect(() => {
    if (currentPage === 1) return;
    scrollViewRef.current?.scrollTo({ y: 0, animated: false });
  }, [currentPage]);

  const handleContinue = async () => {
    const currentPageFields = PROFILE_FORM_FIELD_PAGES[currentPage - 1] ?? [];
    const valid = await trigger([...currentPageFields], { shouldFocus: true });
    if (valid && currentPage < PROFILE_FORM_FIELD_PAGES.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleBack = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const formatDateForBackend = (mmddyyyy: string): string => {
    const [month, day, year] = mmddyyyy.split("/");
    return `${year}-${month}-${day}T00:00:00Z`;
  };

  const onFormSubmit = handleSubmit(async (data) => {
    if (zipcodeError) {
      setFocus("zipCode");
      return;
    }
    if (isValidatingZipcode) return;

    const profileData: UserProfileRequest = {
      preferred_name: data.preferredName || null,
      gender_identity: (data.genderIdentity as GenderIdentity) || null,
      pronouns: (data.pronouns as Pronoun) || null,
      email: data.email && data.email.trim() ? data.email.trim() : undefined,
      state_of_residence: data.stateOfResidence || data.state,
      home_address: {
        street_address: data.streetAddress,
        street_address_2: data.streetAddress2 || undefined,
        city: data.city,
        state: data.state,
        zip_code: data.zipCode,
      },
      ...(isIdVerified
        ? {}
        : {
            first_name: data.firstName,
            last_name: data.lastName,
            date_of_birth: data.dateOfBirth
              ? formatDateForBackend(data.dateOfBirth)
              : undefined,
            sex_at_birth: data.sexAssignedAtBirth as any,
          }),
    };
    upsertProfile(
      {
        data: profileData,
      },
      {
        onSuccess,
        onError: (error) => {
          if (error instanceof AxiosError && error.response?.status === 409) {
            show(
              "Your identity is verified. Please contact support to change locked fields.",
            );
            queryClient.invalidateQueries({
              queryKey: getGetUserProfileQueryKey(),
            });
            return;
          }
          show("Something went wrong. Please try again.");
          onError();
        },
      },
    );
  });

  const fields = (
    <YStack flex={1}>
      <YStack display={currentPage === 1 ? "flex" : "none"}>
        <PersonalInfoFields
          control={control}
          errors={errors}
          isIdVerified={isIdVerified}
        />
      </YStack>

      <YStack display={currentPage === 2 ? "flex" : "none"} gap="$150">
        <Header gap="$100">
          <Heading size="$xl">Add your address</Heading>
          <Paragraph color="$textSecondary" size="$s">
            We'll use it for any prescriptions or lab orders.
          </Paragraph>
        </Header>
        <AddressFields
          control={control}
          errors={errors}
          setValue={setValue}
          zipcodeError={zipcodeError}
        />
      </YStack>
    </YStack>
  );

  const footer = (
    <View
      flexDirection="column"
      padding="$200"
      gap="$200"
      $lg={{
        flexDirection: "row-reverse",
        alignItems: "center",
        gap: "$300",
      }}
    >
      {currentPage === 1 ? (
        <Button
          mode="primary"
          constrained
          onPress={handleContinue}
          dataTrackingId="profile-builder-personal-info-continue-button"
        >
          Continue
        </Button>
      ) : (
        <Button
          mode="primary"
          constrained
          dataTrackingId="profile-builder-location-info-submit-button"
          onPress={onFormSubmit}
          loading={isSubmitting || isValidatingZipcode}
        >
          Continue
        </Button>
      )}
      <Paragraph
        size="$xs"
        color="$textSecondary"
        textAlign="center"
        fontStyle="italic"
        $lg={{ flex: 1, textAlign: "left" }}
      >
        {`${shortBrandName} may validate your ID and share your information for this purpose with a third party.`}
      </Paragraph>
    </View>
  );

  if (renderLayout) {
    return renderLayout({
      fields,
      footer,
      currentPage,
      handleBack,
      scrollViewRef,
    });
  }

  // Default standalone layout (used by AI doctor intake flow)
  return (
    <ScrollView ref={scrollViewRef} flex={1}>
      {currentPage > 1 && (
        <Button.Wrapper
          onPress={handleBack}
          aria-label="Back"
          alignItems="center"
          justifyContent="center"
          alignSelf="flex-start"
        >
          <ChevronLeft size="$300" />
        </Button.Wrapper>
      )}
      {fields}
      {footer}
    </ScrollView>
  );
};
